'use client';

import type { ClassValue } from 'clsx';
import type { CustomerSupportT } from '../../../type/customerSupport';
import { IconMailCustomerSupport, IconPhoneCustomerSupport } from '@vs-shop/ui/icons';
import {
  SupportCard,
  SupportCardAvatar,
  SupportCardChatButton,
  SupportCardContact,
  SupportCardContactVariant,
  SupportCardContactText,
  SupportCardGround,
  SupportCardHours,
  SupportCardName,
  SupportCardPhoto,
  SupportCardPresence,
  SupportCardPresenceState,
  SupportCardSurround,
  SupportCardText,
  type SupportCardEdge,
  type SupportCardHang,
} from '@vs-shop/commerce-ui/supportCard';
import { useIntl } from 'react-intl';

import { clsxm } from '@vs-shop/utils';

import { customerSupportNameConcat } from '../../../utils/customerSupportNameConcat';

import ImageSupportProfilePicture from '../../../assets/customerSupport/order-customer-support.png';
import {
  openSupportBoxFn,
  supportBoxIsOnlineAtom,
} from '@vs-shop/engine/atom/supportBox/supportBoxAtoms';
import { useAtomSet, useAtomValue } from '@effect/atom-react';

type Props = {
  customerSupport: CustomerSupportT;
  isCustomerSupportOnline: boolean;
  /** The ground the card itself sits on. */
  ground: SupportCardGround;
  edge: SupportCardEdge;
  hang: SupportCardHang;
  className?: ClassValue;
};

/**
 * The advisor's card as this storefront composes it, in a column.
 *
 * Every page that carries it puts it on the ground the page itself is NOT, so
 * the frame around the photograph — which paints what stands behind the card,
 * not what is under it — takes the other ground of the two.
 */
const surroundBehind = (ground: SupportCardGround): SupportCardSurround =>
  ground === SupportCardGround.BASE ? SupportCardSurround.INTENSE : SupportCardSurround.BASE;

export default function CustomerSupportLarge({
  customerSupport,
  isCustomerSupportOnline,
  ground,
  edge,
  hang,
  className,
}: Props) {
  const intl = useIntl();

  const isChatOnline = useAtomValue(supportBoxIsOnlineAtom);
  const openChat = useAtomSet(openSupportBoxFn);

  const { email, telReadable, telValue, openingHoursShort } = customerSupport;

  const presence = isCustomerSupportOnline
    ? SupportCardPresenceState.ONLINE
    : SupportCardPresenceState.OFFLINE;

  return (
    <SupportCard
      variant="large"
      ground={ground}
      edge={edge}
      hang={hang}
      className={clsxm(className)}
    >
      {/* IMAGE */}
      <div className="inset-x-0 -mt-[75px] mb-6 flex w-full justify-center">
        <SupportCardAvatar variant="large" surround={surroundBehind(ground)} presence={presence}>
          <SupportCardPhoto
            src={ImageSupportProfilePicture.src}
            alt={customerSupportNameConcat(customerSupport)}
            preload
            width={100}
            height={100}
          />
        </SupportCardAvatar>
      </div>

      <div className="mb-7 flex flex-col items-center justify-center">
        {/* NAME */}
        <div className="mb-1.5 flex items-center gap-2">
          <SupportCardPresence variant="inline" presence={presence} />
          <SupportCardName>{customerSupportNameConcat(customerSupport)}</SupportCardName>
        </div>

        {/* CUSTOMER SUPPORT */}
        <SupportCardText className="mb-4">
          {intl.formatMessage({
            id: 'customerSupport.customerSupport',
            defaultMessage: 'ZÁKAZNICKÁ PODPORA',
          })}
        </SupportCardText>

        {/* PHONE */}
        <SupportCardContact
          href={`tel:${telValue}`}
          title={intl.formatMessage({
            id: 'customerSupport.phone',
            defaultMessage: 'RCprofi zákaznická podpora telefon',
          })}
          className="mb-1.5"
          variant={SupportCardContactVariant.CLASSIC}
        >
          <IconPhoneCustomerSupport className="shrink-0 text-foreground/50" />
          <SupportCardContactText variant="large">{telReadable}</SupportCardContactText>
        </SupportCardContact>

        {/* EMAIL */}
        <SupportCardContact
          href={`mailto:${email}`}
          title={intl.formatMessage({
            id: 'customerSupport.email',
            defaultMessage: 'RCprofi zákaznická podpora e-mail',
          })}
          className="mb-2"
          variant={SupportCardContactVariant.CLASSIC}
        >
          <IconMailCustomerSupport className="shrink-0 text-foreground/50" />
          <SupportCardContactText variant="large">{email}</SupportCardContactText>
        </SupportCardContact>

        {/* OPENING HOURS */}
        <SupportCardHours variant="large">{openingHoursShort}</SupportCardHours>
      </div>

      {/* BUTTON */}
      <div className="flex flex-col">
        <SupportCardChatButton
          presence={
            isChatOnline ? SupportCardPresenceState.ONLINE : SupportCardPresenceState.OFFLINE
          }
          onClick={() => {
            openChat();
          }}
        >
          {isChatOnline
            ? intl.formatMessage({
                id: 'customerSupport.chat.online',
                defaultMessage: 'ONLINE CHAT',
              })
            : intl.formatMessage({
                id: 'customerSupport.chat.offline',
                defaultMessage: 'OFFLINE CHAT',
              })}
        </SupportCardChatButton>
      </div>
    </SupportCard>
  );
}
